<template>
  <div>
    <header class="topbar">
      <div>
        <div class="topTitle">{{ pageTitle }}</div>
      </div>
      <div class="topRight">
        <el-button size="small" native-type="button" v-has="'btn_add'" @click="showStaffCreateModal">
          <span class="topActionPlus">+</span> 添加面试官
        </el-button>
      </div>
    </header>

    <CommonFilterTableCard
      title="👤 面试官管理"
      :items="interviewers"
      :page.sync="page.pageNo"
      :page-size.sync="page.pageSize"
      :use-server-pagination="true"
      :total="interviewerTotal"
      :loading="tableLoading"
      :filter-schema="interviewerFilterSchema"
      :filter-values.sync="interviewerFilters"
      empty-text="暂无面试官"
      :columns="interviewerColumns"
      @page-change="fetchInterviewerList"
      @filter-change="onInterviewerFilterChange"
    >
      <template #col-email="{ row }">
        {{ row.email || "—" }}
      </template>

      <template #col-status="{ row }">
        <span v-if="row && row.status != null && String(row.status).trim() !== ''" :class="['status-badge', staffStatusClass(normalizeStaffStatus(row.status))]">
          {{ normalizeStaffStatus(row.status) }}
        </span>
        <span v-else></span>
      </template>

      <template #col-actions="{ row }">
        <el-button size="small" native-type="button" v-has="'btn_edit'" class="btnLink small" @click="showStaffEditModal(row)">编辑</el-button>
        <el-button size="small" native-type="button" v-has="'btn_delete'" class="btnLink danger small" @click="deleteInterviewer(row)">删除</el-button>
      </template>
    </CommonFilterTableCard>

    <!-- 添加/编辑弹窗 -->
    <div v-if="showStaffModal" class="modal-overlay">
      <div class="modal-content hr-like-modal">
        <div class="modal-header">
          <h3>{{ staffModalTitle }}</h3>
          <button type="button" class="modal-close-btn" aria-label="关闭" @click="closeStaffModal">&times;</button>
        </div>
        <div class="modal-body">
          <label class="required-label">选择人员</label>
          <!-- 添加：选择人员下拉 -->
          <el-select
            v-if="staffModalMode === 'create'"
            v-model="staffForm.oaUserId"
            filterable
            remote
            :remote-method="remoteOaUserSearch"
            :loading="oaUserLoading"
            clearable
            placeholder="请选择"
            style="width: 100%"
            :class="{ 'field-error': staffErrors.oaUserId }"
            @change="onOaUserSelect"
            @clear="onOaUserClear"
          >
            <el-option
              v-for="u in oaUserOptions"
              :key="u.userId"
              :label="formatOaLabel(u)"
              :value="u.userId"
            />
          </el-select>
          <!-- 编辑：只读回显（来自列表数据：姓名 + 工号） -->
          <input
            v-else
            v-model="selectedPersonLabel"
            disabled
            :class="{ 'field-error': staffErrors.oaUserId }"
            type="text"
          />
          <div v-if="staffErrors.oaUserId" class="error-tip">{{ staffErrors.oaUserId }}</div>
          <label class="required-label">电话</label>
          <input
            v-model="staffForm.phone"
            maxlength="11"
            :disabled="staffModalMode === 'create'"
            :class="{ 'field-error': staffErrors.phone }"
            type="tel"
            placeholder="请输入"
            @blur="onStaffFieldBlur('phone')"
          />
          <div v-if="staffErrors.phone" class="error-tip">{{ staffErrors.phone }}</div>
          <label class="required-label">邮箱</label>
          <input
            v-model="staffForm.email"
            maxlength="50"
            :disabled="staffModalMode === 'create'"
            :class="{ 'field-error': staffErrors.email }"
            type="email"
            placeholder="请输入"
            @blur="onStaffFieldBlur('email')"
          />
          <div v-if="staffErrors.email" class="error-tip">{{ staffErrors.email }}</div>
        </div>
        <div class="modal-actions">
          <el-button native-type="button" @click="closeStaffModal">取消</el-button>
          <el-button type="primary" native-type="button" @click="confirmStaffModal">确认</el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CommonFilterTableCard from "../components/CommonFilterTableCard.vue";
import { addInterviewer, editInterviewer, listInterviewers, removeInterviewer, searchOaUsers } from "@/api/modules/users/user";
import { isEmail, isMobile11 } from "@/utils/validator";

export default {
  name: "InterviewerManagePage",
  components: { CommonFilterTableCard },
  data() {
    return {
      page: { pageNo: 1, pageSize: 10 },
      interviewerFilters: { search: "", status: "all" },
      interviewerFilterSchema: [
        {
          label: "员工状态",
          field: "status",
          type: "select",
          options: [
            { label: "全部", value: "all" },
            { label: "正式", value: "Formal" },
            { label: "试用期", value: "Try" },
            { label: "实习期", value: "Intern" },
            { label: "待离职", value: "WaitLeave" },
            { label: "已离职", value: "Leaved" },
          ],
        },
        {
          label: "姓名/工号",
          field: "search",
          type: "input",
          placeholder: "请输入",
        },
      ],
      tableLoading: false,
      interviewerTotal: 0,
      interviewerRows: [],
      showStaffModal: false,
      staffModalMode: "create", // create | edit
      staffEditingId: null,
      staffErrors: {},
      staffValidationActive: false,
      oaUserOptions: [],
      oaUserLoading: false,
      oaUserQueryTimer: null,
      staffForm: { oaUserId: "", oaUserName: "", empId: "", phone: "", email: "", roleType: "Interviewer" },
    };
  },
  computed: {
    pageTitle() {
      return (this.$route.meta && this.$route.meta.title) || "面试官管理";
    },
    pageSub() {
      return (this.$route.meta && this.$route.meta.subTitle) || "";
    },
    interviewerColumns() {
      return [
        { label: "姓名", prop: "name", minWidth: 100 },
        { label: "工号", prop: "work_no", minWidth: 110 },
        { label: "电话", prop: "phone", minWidth: 120 },
        { label: "邮箱", slot: "email", minWidth: 180 },
        { label: "员工状态", slot: "status", minWidth: 100 },
        { label: "操作", slot: "actions", minWidth: 140, fixed: "right" },
      ];
    },
    interviewers() {
      return this.interviewerRows;
    },
    staffModalTitle() {
      return this.staffModalMode === "edit" ? "编辑面试官" : "添加面试官";
    },
    selectedPersonLabel() {
      const n = this.staffForm && this.staffForm.oaUserName ? String(this.staffForm.oaUserName).trim() : "";
      const w = this.staffForm && this.staffForm.empId ? String(this.staffForm.empId).trim() : "";
      if (n && w) return `${n}（${w}）`;
      return n || w;
    },
  },
  watch: {
    "staffForm.oaUserId"() {
      this.revalidateStaffField("oaUserId");
    },
    "staffForm.phone"() {
      this.revalidateStaffField("phone");
    },
    "staffForm.email"() {
      this.revalidateStaffField("email");
    },
  },
  methods: {
    formatOaLabel(user) {
      const name = (user && user.name) || "";
      const workNo = (user && user.workNo) || "";
      if (name && workNo) return `${name}（${workNo}）`;
      return name || workNo || "";
    },
    /**
     * 员工状态标准化映射
     * Formal-正式, Try-试用期, Intern-实习期, Leaved-已离职, WaitLeave-待离职
     * @param {string} status 状态英文码或中文文案
     * @returns {string} 标准中文状态文案
     */
    normalizeStaffStatus(status) {
      const map = {
        Formal: "正式",
        Try: "试用期",
        Intern: "实习期",
        Leaved: "已离职",
        WaitLeave: "待离职",
        Waitleave: "待离职",
      };
      const raw = String(status || "").trim();
      return map[raw] || raw || "—";
    },
    async fetchInterviewerList(pageNum) {
      this.tableLoading = true;
      try {
        if (pageNum != null) {
          this.page.pageNo = parseInt(pageNum, 10) || 1;
        }
        this.page.pageSize = parseInt(this.page.pageSize, 10) || 10;
        const statusVal = this.interviewerFilters && this.interviewerFilters.status;
        const params = {
          page_num: this.page.pageNo,
          page_size: this.page.pageSize,
          query: (this.interviewerFilters.search || "").trim(),
          ...(statusVal && statusVal !== "all" ? { status: statusVal } : {}),
        };
        const res = await listInterviewers(params);
        this.interviewerRows = Array.isArray(res && res.rows) ? res.rows : [];
        this.interviewerTotal = typeof (res && res.total) === "number" ? res.total : 0;
      } catch (e) {
        this.interviewerRows = [];
        this.interviewerTotal = 0;
      } finally {
        this.tableLoading = false;
      }
    },
    onInterviewerFilterChange() {
      this.page.pageNo = 1;
      this.fetchInterviewerList(1);
    },
    staffStatusClass(status) {
      if (status === "正式") return "status-official";
      if (status === "试用期") return "status-probation";
      if (status === "实习期") return "status-probation";
      if (status === "已离职") return "status-resigned";
      if (status === "待离职") return "status-pending";
      return "status-resigned";
    },
    /**
     * 校验添加/编辑面试官单字段
     * @param {string} field 字段名
     * @returns {boolean} 是否校验通过
     */
    validateStaffField(field) {
      let error = "";
      switch (field) {
        case "oaUserId":
          if (!String(this.staffForm.oaUserId || "").trim()) error = "请选择工号";
          break;
        case "phone":
          if (!String(this.staffForm.phone || "").trim()) error = "请输入电话";
          else if (!isMobile11(this.staffForm.phone)) error = "请输入正确的手机号（11位）";
          break;
        case "email":
          if (!String(this.staffForm.email || "").trim()) error = "请输入邮箱";
          else if (!isEmail(this.staffForm.email)) error = "请输入正确的邮箱";
          break;
        default:
          break;
      }
      if (error) this.$set(this.staffErrors, field, error);
      else this.$delete(this.staffErrors, field);
      return !error;
    },
    /**
     * 校验添加/编辑面试官表单
     * @returns {boolean} 是否校验通过
     */
    validateStaffForm() {
      // 首次点击确认时再整体激活校验
      this.staffValidationActive = true;
      const fields = ["oaUserId", "phone", "email"];
      let pass = true;
      fields.forEach((field) => {
        const ok = this.validateStaffField(field);
        if (!ok) pass = false;
      });
      return pass;
    },
    /**
     * 字段变化后重校验
     * @param {string} field 字段名
     */
    revalidateStaffField(field) {
      if (!this.staffValidationActive) return;
      this.validateStaffField(field);
    },
    remoteOaUserSearch(query) {
      const q = (query && String(query).trim()) || "00";
      if (this.oaUserQueryTimer) clearTimeout(this.oaUserQueryTimer);
      this.oaUserQueryTimer = setTimeout(() => {
        this.fetchOaUserOptions(q);
      }, 300);
    },
    async fetchOaUserOptions(query = "00") {
      this.oaUserLoading = true;
      try {
        const res = await searchOaUsers({
          query: query || "00",
          page_num: 1,
          page_size: 10,
        });
        const list = Array.isArray(res) ? res : [];
        const currentId = this.staffForm && this.staffForm.oaUserId;
        if (currentId) {
          const found = list.find((u) => String(u.userId) === String(currentId));
          const patch = {
            workNo: this.staffForm.empId || (found && found.workNo) || "",
            name: this.staffForm.oaUserName || (found && found.name) || "",
            pboneNum: this.staffForm.phone || (found && (found.pboneNum || found.phoneNum)) || "",
            email: this.staffForm.email || (found && found.email) || "",
          };
          if (found) {
            Object.assign(found, patch);
          } else {
            list.unshift({ userId: currentId, ...patch });
          }
        }
        this.oaUserOptions = list;
      } catch (e) {
        this.oaUserOptions = [];
      } finally {
        this.oaUserLoading = false;
      }
    },
    onOaUserSelect() {
      const selected = this.oaUserOptions.find((u) => String(u.userId) === String(this.staffForm.oaUserId));
      if (!selected) return;
      const pickedWorkNo = selected.workNo || "";
      this.staffForm.oaUserName = selected.name || "";
      this.staffForm.empId = pickedWorkNo;
      this.staffForm.phone = selected.pboneNum || selected.phoneNum || "";
      this.staffForm.email = selected.email || "";
    },
    onOaUserClear() {
      // 清空选择人员时，同步清空其余关联字段
      this.staffForm.oaUserId = "";
      this.staffForm.oaUserName = "";
      this.staffForm.empId = "";
      this.staffForm.phone = "";
      this.staffForm.email = "";
      // 认为用户已经开始真实编辑，开启校验并立即触发必填检查
      this.staffValidationActive = true;
      this.validateStaffField("oaUserId");
      this.validateStaffField("phone");
      this.validateStaffField("email");
    },
    onStaffFieldBlur(field) {
      // 用户手动编辑/清空电话或邮箱后离开输入框时，开启校验并立刻校验该字段
      this.staffValidationActive = true;
      this.validateStaffField(field);
    },
    showStaffCreateModal() {
      this.staffModalMode = "create";
      this.staffEditingId = null;
      this.staffErrors = {};
      // 打开弹框时先不激活校验，避免一进来就全红
      this.staffValidationActive = false;
      this.staffForm = {
        oaUserId: "",
        oaUserName: "",
        empId: "",
        phone: "",
        email: "",
        roleType: "Interviewer",
      };
      this.showStaffModal = true;
      this.fetchOaUserOptions("00");
    },
    showStaffEditModal(p) {
      this.staffModalMode = "edit";
      this.staffEditingId = p.userid || p.id;
      this.staffErrors = {};
      // 打开弹框时先不激活校验，避免一进来就全红
      this.staffValidationActive = false;
      const workNo = p.work_no || p.empId || "";
      this.staffForm = {
        oaUserId: p.userid || p.id || "",
        oaUserName: p.name || "",
        empId: workNo,
        phone: p.phone || "",
        email: p.email || "",
        roleType: p.role_type || "Interviewer",
      };
      this.showStaffModal = true;
    },
    closeStaffModal() {
      this.showStaffModal = false;
      // 关闭时重置校验状态与错误
      this.staffValidationActive = false;
      this.staffErrors = {};
      if (this.oaUserQueryTimer) {
        clearTimeout(this.oaUserQueryTimer);
        this.oaUserQueryTimer = null;
      }
    },
    confirmStaffModal() {
      const phone = (this.staffForm.phone || "").trim();
      const email = (this.staffForm.email || "").trim();
      const userid = (this.staffForm.oaUserId || "").trim();

      if (!this.validateStaffForm()) {
        return;
      }

      if (this.staffModalMode === "edit" && this.staffEditingId != null) {
        // 编辑面试官：走后端接口，仅允许修改电话/邮箱
        this.updateInterviewer({ userid, phone, email });
      } else {
        this.createInterviewer({ userid, roleType: this.staffForm.roleType });
        return;
      }

      this.closeStaffModal();
    },
    /**
     * 添加面试官
     * @param {{userid: string, roleType: string}} payload 表单字段
     */
    async createInterviewer(payload) {
      const params = {
        userid: payload.userid,
        role_type: payload.roleType,
      };
      try {
        await addInterviewer(params);
        this.$message.success("面试官添加成功");
        this.closeStaffModal();
        this.page.pageNo = 1;
        await this.fetchInterviewerList(1);
      } catch (e) {}
    },
    /**
     * 编辑面试官（只允许更新电话/邮箱）
     * @param {{userid: string, phone: string, email: string}} payload
     */
    async updateInterviewer(payload) {
      const params = {
        userid: payload && payload.userid,
        phone: payload && payload.phone,
        email: payload && payload.email,
      };
      try {
        await editInterviewer(params);
        this.$message.success("面试官信息已更新");
        this.closeStaffModal();
        await this.fetchInterviewerList(this.page.pageNo);
      } catch (e) {}
    },
    async deleteInterviewer(p) {
      const targetId = p && (p.userid || p.id);
      if (!targetId) return;
      this.$confirm("面试官删除后不可登录", "提示", { type: "warning" })
        .then(async () => {
          try {
            await removeInterviewer({ userid: targetId });
            this.$message.success("面试官已移除");
            this.page.pageNo = 1;
            await this.fetchInterviewerList(1);
          } catch (e) {
            
          }
        })
        .catch(() => {});
    },
  },
  created() {
    this.fetchInterviewerList(this.page.pageNo);
  },
};
</script>

<style scoped>
.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
  height: 33px;
}

.topTitle {
  font-size: 18px;
  font-weight: 900;
}

.topSub {
  font-size: 12px;
  color: var(--muted);
  margin-top: 2px;
}

.topRight {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.topActionPlus {
  display: inline-block;
  font-size: 13px;
  font-weight: 400;
  line-height: 1;
  transform: scale(1.35);
  transform-origin: center;
  margin-right: 4px;
}

.modal-body input.field-error,
.modal-body select.field-error {
  border-color: #f56c6c !important;
}

.modal-body input:disabled {
  background-color: #f5f7fa;
  border-color: #e4e7ed;
  color: #c0c4cc;
  cursor: not-allowed;
}

.modal-body /deep/ .el-select.field-error .el-input__inner {
  border-color: #f56c6c !important;
}

/* 弹框内选择人员下拉：保证清空图标垂直居中 */
.modal-body /deep/ .el-select .el-input__inner {
  height: 36px;
  line-height: 36px;
}

.modal-body /deep/ .el-select .el-input__suffix {
  /* 让 suffix 不再撑满整个 el-input，避免 clear 图标偏下 */
  display: block;
  height: 36px;
}

.modal-body /deep/ .el-select .el-input__suffix-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
}

.error-tip {
  color: #f56c6c;
  font-size: 12px;
  margin-top: -8px;
  margin-bottom: 8px;
}

.required-label::before {
  content: "*";
  color: #f56c6c;
  margin-right: 4px;
}
</style>

